import type { ReactNode } from 'react';
import type { StaticImageData } from 'next/image';
import Image from 'next/image';
import { clsx } from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import { FiCheck } from 'react-icons/fi';

import { Box, Divider, Flex, Stack, Text, Title, withErrorBoundary } from '@highlight-cp/core-ui';

import classes from './zero-state-marketing.module.css';

interface ZeroStateMarketingProps {
  heading: string;
  subtitle: string | ReactNode;
  image?: StaticImageData | string;
  /** Override the rendered width of the hero image. Defaults to 634. */
  imageWidth?: number;
  /** Override the rendered height of the hero image. Defaults to 684. */
  imageHeight?: number;
  /** Additional CSS module class applied to the image container Box for custom positioning.
   *  Skips the default slide-in animation — supply your own animation in the class if needed. */
  imageContainerClassName?: string;

  featuresTitle?: string;
  features?: { text: string; iconClass: string | undefined }[];
  actions?: ReactNode;
}

/**
 * ### ZeroStateMarketing
 *
 * @description
 * Empty state component for the marking a feature with gradient background,
 * explanatory text, feature highlights, and a hero image
 *
 * @example
 * <ZeroStateMarketing heading={t('Heading')} subtitle={"Amazing feature to try"} image={imageSrc} features={[{title: 'feature 1', iconClass: 'classname' }]} />
 *
 * @param {string} heading - The heading to display in the component.
 * @param {string} subtitle - The subtile of items to show.
 * @param {string} [image] - Hero image that displays on the right. Optional.
 * @param {string} [featuresTitle] - Title for the features list, defaults to 'Customization'.
 * @param {ZeroStateMarketingProps['features']} [features] - List of features to market.
 *
 *
 * @returns {JSX.Element}
 */
export const ZeroStateMarketing = withErrorBoundary(
  (props: ZeroStateMarketingProps) => {
    const {
      actions,
      heading,
      subtitle,
      image,
      imageWidth = 634,
      imageHeight = 684,
      imageContainerClassName,
      featuresTitle,
      features = [],
    } = props;
    const { t } = useTranslation();

    return (
      <Box className={classes.container}>
        <Flex className={classes.content} gap={100} align="center">
          <Stack gap={48}>
            <Stack gap={24}>
              <h1 className={classes.mainHeading}>{heading}</h1>

              <Box className={classes.subtitle}>
                <Text size="xl" c="white">
                  {subtitle}
                </Text>
              </Box>
            </Stack>

            {!!actions && actions}

            {!!features?.length && (
              <Stack gap={0}>
                <Box>
                  <Box className={classes.featuresTitle}>
                    <Title order={5} c="white">
                      {featuresTitle ?? t('Customization')}
                    </Title>
                  </Box>
                  <Box className={classes.dividerLine}>
                    <Divider />
                  </Box>
                  <Stack gap={14}>
                    {features.map((feature) => (
                      <Flex
                        key={feature.text}
                        align="center"
                        gap={10}
                        className={classes.featureItem}
                      >
                        <Box className={clsx(classes.featureIcon, feature.iconClass)}>
                          <FiCheck className={classes.checkIcon} />
                        </Box>
                        <Text size="lg" c="white">
                          {feature.text}
                        </Text>
                      </Flex>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            )}
          </Stack>
        </Flex>

        {!!image && (
          <Box
            className={clsx(
              classes.imageContainer,
              !imageContainerClassName && classes.imageContainerAnimated,
              imageContainerClassName,
            )}
          >
            <Image
              src={image}
              alt={t('Image of a computer with a report on the screen')}
              height={imageHeight}
              width={imageWidth}
              quality={100}
            />
          </Box>
        )}
      </Box>
    );
  },
  {
    componentName: 'zero state marking',
  },
);
